import { Procedure, ProcedureRecurrenceType, Project } from "@shared/models";
import { z } from "zod";
import { Option } from "@shared/interfaces";
import { CreateProcedureDto, ProcedureFormShape } from "@containers/Procedure/interfaces";
import { FORM_ERROR_MESSAGES, WEBSITE_LINK } from "@shared/constants";
import { actions as projectActions, selectors as projectSelectors } from "@containers/Project/store";

export const PROCEDURE_STATUS_OPTIONS: Option<string>[] = [
  { value: "Published", label: "Published" },
  { value: "Unpublished", label: "Unpublished" },
];

const optionValidationSchema = z.object({ value: z.string(), label: z.string() });

/**
 * Form rules. `project` carries no rule: an unpicked project is accepted and sent as `project_id: NaN`
 * (see `prepareSubmitValue`).
 */
export const validationSchema = z.object({
  name: z.string().min(1, FORM_ERROR_MESSAGES.REQUIRED).max(250, `Procedure name ${FORM_ERROR_MESSAGES.LONG}`),
  status: z.string({ error: FORM_ERROR_MESSAGES.REQUIRED }).min(1, FORM_ERROR_MESSAGES.REQUIRED),
  // Rule order matters: an empty link reports "required", a short one "too short", a long non-URL the link message.
  external_link: z
    .string()
    .min(1, FORM_ERROR_MESSAGES.REQUIRED)
    .min(10, `Link ${FORM_ERROR_MESSAGES.SHORT}`)
    .regex(WEBSITE_LINK, "Please provide a valid link"),
  process: z.number({ error: FORM_ERROR_MESSAGES.REQUIRED }),
  accountable_team_id: z.number({ error: FORM_ERROR_MESSAGES.REQUIRED }),
  teams: z.array(z.number()).min(1, FORM_ERROR_MESSAGES.REQUIRED),
  project: optionValidationSchema.nullable(),
  recurrence: z.string({ error: FORM_ERROR_MESSAGES.REQUIRED }).min(1, FORM_ERROR_MESSAGES.REQUIRED),
}) satisfies z.ZodType<ProcedureFormShape>;

/** Form values for a procedure; `null` gives the create-mode defaults (Unpublished, recurrence None). */
export const getInitValues = (procedure: Procedure | null): ProcedureFormShape => ({
  name: procedure?.name || "",
  status: procedure?.status || PROCEDURE_STATUS_OPTIONS[1].value,
  external_link: procedure?.external_link || "",
  process: procedure?.process[0]?.id || null,
  accountable_team_id: procedure?.accountable_team_id || null,
  teams: procedure?.teams?.map((team) => team.id) || [],
  project: getProjectInitValues(procedure),
  recurrence: getRecurrenceInitValue(procedure),
});

/** Maps the parsed form output to the create/update request body. */
export const prepareSubmitValue = (value: z.output<typeof validationSchema>): CreateProcedureDto => ({
  name: value.name,
  status: value.status,
  external_link: value.external_link,
  process: value.process,
  accountable_team_id: value.accountable_team_id,
  teams: value.teams,
  project_id: Number(value.project?.value),
  recurrence: value.recurrence,
});

export const getProjectInitValues = (procedure: Procedure | null) => {
  if (!procedure || !procedure.project) return null;

  return {
    label: procedure.project.name,
    value: String(procedure.project.id),
  };
};

export const getRecurrenceInitValue = (procedure: Procedure | null) => {
  if (!procedure) return ProcedureRecurrenceType.NONE;

  return procedure.recurrence;
};

export const handlers = {
  project: {
    selectData: projectSelectors.getProjects,
    loadingAction: projectActions.getProjects,
    prepareOptionFunction: (item: Project): Option => {
      return { label: item.name, value: String(item.id) };
    },
    getData: (search: string) => {
      return projectActions.getProjects.request({
        search,
        page: 0,
        limit: 20,
      });
    },
  },
};
